import {PubSub} from './Pubsub';

type StorageData<T = any> = {
  data: Record<string, T>;
  expirationTime: number;
};
class ExpirationStorage<T = any> extends PubSub {
  private storageKey: string;
  private defaultExpirationDays: number;

  constructor(key: string, expirationDays: number = 30) {
    super();
    this.storageKey = key;
    this.defaultExpirationDays = expirationDays;

    // 初始化时检查现有数据
    this.initializeStorage();
  }

  private initializeStorage(): void {
    const stored = localStorage.getItem(this.storageKey);

    if (!stored) {
      // 不存在，创建新的
      this.createNewStorage();
    } else {
      try {
        const parsedData: StorageData<T> = JSON.parse(stored);

        // 检查是否过期
        if (this.isExpired(parsedData.expirationTime)) {
          // 过期了，重新创建
          this.createNewStorage();
        }
        // 如果没有过期，什么都不做，保持现有数据
      } catch (error) {
        // 解析失败，重新创建
        console.warn(
          'Failed to parse stored data, creating new storage:',
          error,
        );
        this.createNewStorage();
      }
    }
  }

  private createNewStorage(data: any = {}): void {
    const storageData: StorageData<T> = {
      data,
      expirationTime: this.calculateExpirationTime(this.defaultExpirationDays),
    };

    localStorage.setItem(this.storageKey, JSON.stringify(storageData));
  }

  /**
   * 计算过期时间，返回第N天的0点时间戳
   * @param days 天数
   * @returns 时间戳
   */
  private calculateExpirationTime(days: number): number {
    const now = new Date();
    const expirationDate = new Date(now);

    // 设置为第N天后的0点
    expirationDate.setDate(now.getDate() + days);
    expirationDate.setHours(0, 0, 0, 0);

    return expirationDate.getTime();
  }

  private isExpired(expirationTime: number): boolean {
    return new Date().getTime() > expirationTime;
  }

  // 获取存储的数据
  get(): Record<string, T> | null {
    const stored = localStorage.getItem(this.storageKey);

    if (!stored) {
      return null;
    }

    try {
      const parsedData: StorageData<T> = JSON.parse(stored);

      if (this.isExpired(parsedData.expirationTime)) {
        // 过期了，清除并返回null
        this.clear();
        return null;
      }

      return parsedData.data;
    } catch (error) {
      console.warn('Failed to get stored data:', error);
      return null;
    }
  }

  // 设置数据
  set(data: Record<string, T>): void {
    this.createNewStorage(data);
  }

  // 设置数据并指定过期天数
  setWithExpiration(data: Record<string, T>, days: number): void {
    const storageData: StorageData<T> = {
      data,
      expirationTime: this.calculateExpirationTime(days),
    };

    localStorage.setItem(this.storageKey, JSON.stringify(storageData));
  }

  // 更新数据（保持原有的过期时间）
  update(data: Record<string, T>): void {
    const stored = localStorage.getItem(this.storageKey);

    if (stored) {
      try {
        const parsedData: StorageData<T> = JSON.parse(stored);

        if (!this.isExpired(parsedData.expirationTime)) {
          // 没有过期，更新数据但保持过期时间
          const updatedData: StorageData<T> = {
            data,
            expirationTime: parsedData.expirationTime,
          };
          localStorage.setItem(this.storageKey, JSON.stringify(updatedData));
          return;
        }
      } catch (error) {
        console.warn('Failed to update stored data:', error);
      }
    }

    // 如果没有存储数据或者过期了，创建新的
    this.createNewStorage(data);
  }

  // 清除存储
  clear(): void {
    console.log('clear', this.storageKey);
    localStorage.removeItem(this.storageKey);
  }

  // 检查是否存在且未过期
  isValid(): boolean {
    const stored = localStorage.getItem(this.storageKey);

    if (!stored) {
      return false;
    }

    try {
      const parsedData: StorageData<T> = JSON.parse(stored);
      return !this.isExpired(parsedData.expirationTime);
    } catch (error) {
      return false;
    }
  }

  // 获取过期时间
  getExpirationTime(): Date | null {
    const stored = localStorage.getItem(this.storageKey);

    if (!stored) {
      return null;
    }

    try {
      const parsedData: StorageData<T> = JSON.parse(stored);
      return new Date(parsedData.expirationTime);
    } catch (error) {
      return null;
    }
  }

  // 重置过期时间
  resetExpiration(days?: number): void {
    const currentData = this.get();
    if (currentData !== null) {
      const expirationDays = days || this.defaultExpirationDays;
      this.setWithExpiration(currentData, expirationDays);
    }
  }

  // 获取距离过期还有多少天
  getDaysUntilExpiration(): number {
    const expirationTime = this.getExpirationTime();
    if (!expirationTime) {
      return 0;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const expiration = new Date(
      expirationTime.getFullYear(),
      expirationTime.getMonth(),
      expirationTime.getDate(),
    );

    const diffTime = expiration.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }
}

export default ExpirationStorage;
